import { AnimatedNumber } from '../AnimatedNumber';
import { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import { money } from './parts';
import type { CoinView } from './model';
import { CHART, chartPoints, chartToChip, chartZone } from './chartGeometry';
import { chartActivity } from './chartActivity';
import { useChartMotion } from './useChartMotion';
import { ChartAtmosphere } from './ChartAtmosphere';
const { width: WIDTH, height: HEIGHT, baseline: BASELINE } = CHART;
export function CoinChart({ coin }: { coin: CoinView }) {
  const id = useId();
  const chartRef = useRef<HTMLDivElement>(null);
  const chipRef = useRef<HTMLSpanElement>(null);
  const [chipLeft, setChipLeft] = useState(WIDTH - 48);
  const measureChip = useCallback(() => {
    const chart = chartRef.current;
    const chip = chipRef.current;
    // Layout coordinates ignore the card's entrance scale and the chip pulse.
    if (chart && chip && chart.clientWidth > 0)
      setChipLeft((chip.offsetLeft / chart.clientWidth) * WIDTH);
  }, []);
  // A different-length amount must reconnect before paint, not one frame later.
  useLayoutEffect(measureChip, [measureChip, coin.marketCap]);
  useLayoutEffect(() => {
    const chart = chartRef.current;
    const chip = chipRef.current;
    if (!chart || !chip) return;
    const observer = new ResizeObserver(measureChip);
    observer.observe(chart);
    observer.observe(chip);
    return () => observer.disconnect();
  }, [measureChip]);
  const live =
    !coin.stale && (coin.phase === 'open' || coin.phase === 'watching');
  const points = useChartMotion(
    `${coin.id}:${coin.baseline}:${live}`,
    chartPoints(coin.history, coin.baseline),
    // Finish before the 250ms simulation cadence, rather than constantly lagging.
    // Preview cursors retain their independent, slower default motion.
    live ? 200 : 0,
  );
  const line = points ? chartToChip(points, chipLeft) : null;
  const latest = coin.marketCap;
  const zone = chartZone(points);
  // Preserve pulse keys between animation frames and unchanged clock ticks.
  const input = {
    id: coin.id,
    baseline: coin.baseline,
    value: coin.history.at(-1) ?? null,
    zone,
    enabled: live && !!line,
  };
  const [previous, setPrevious] = useState(() => chartActivity(null, input));
  const activity = chartActivity(previous, input);
  if (activity !== previous) setPrevious(activity);
  const tail = line?.tail;
  const rising = line ? line.end <= BASELINE : (coin.changePercent ?? 0) >= 0;
  return (
    <div ref={chartRef} className="coin-chart" data-chart-zone={zone}>
      <ChartAtmosphere zone={zone} />
      {line && (
        <svg
          className="chart-lines"
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          preserveAspectRatio="none"
          aria-hidden="true"
          focusable="false"
        >
          <defs>
            <clipPath id={id + '-up'}>
              <rect x="0" y="0" width={WIDTH} height={BASELINE} />
            </clipPath>
            <clipPath id={id + '-down'}>
              <rect
                x="0"
                y={BASELINE}
                width={WIDTH}
                height={HEIGHT - BASELINE}
              />
            </clipPath>
          </defs>
          <path
            d={line.d}
            className="chart-line is-up"
            clipPath={`url(#${id}-up)`}
            vectorEffect="non-scaling-stroke"
          />
          <path
            d={line.d}
            className="chart-line is-down"
            clipPath={`url(#${id}-down)`}
            vectorEffect="non-scaling-stroke"
          />
          {activity.enabled && activity.quote > 0 && tail && (
            <g key={activity.quote}>
              {(['up', 'down'] as const).map((side) => (
                <path
                  key={side}
                  d={tail}
                  pathLength="100"
                  className={'chart-line chart-tail is-' + side}
                  clipPath={`url(#${id}-${side})`}
                  vectorEffect="non-scaling-stroke"
                />
              ))}
            </g>
          )}
        </svg>
      )}
      <span className="chart-baseline" aria-hidden="true" />
      {activity.enabled && activity.crossing > 0 && (
        <span
          key={activity.crossing}
          className={'chart-crossing is-' + activity.lastZone}
          aria-hidden="true"
        />
      )}
      <span className="chart-baseline-value">
        {/* The line is the graduation reference even when its USD cap was never quoted. */}
        {coin.graduationCap === null ? (
          'At graduation'
        ) : (
          <AnimatedNumber value={money(coin.graduationCap)} />
        )}
      </span>
      <span
        ref={chipRef}
        className={
          'chart-latest ' +
          (line ? (rising ? 'is-up' : 'is-down') : 'is-neutral')
        }
        style={{
          top:
            ((line ? Math.min(HEIGHT - 20, Math.max(20, line.end)) : 45) /
              HEIGHT) *
              100 +
            '%',
        }}
      >
        <AnimatedNumber key={coin.id} value={money(latest)} />
        {activity.enabled && activity.quote > 0 && (
          <span
            key={activity.quote}
            className="chart-quote-pulse"
            aria-hidden="true"
          />
        )}
      </span>
      {!line && (
        <span className="chart-empty">
          {coin.baseline ? 'Collecting price history' : 'No USD baseline yet'}
        </span>
      )}
    </div>
  );
}
